import React from 'react';
import Loading from '../../misc/Loading';
import * as Helper from '../../../util/Helper';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import StatusCode from '../../../util/StatusCode';
import { CoreModule, CoreModuleStatus, CoreModuleTrigger } from '../../../models/CoreModels';
import AuthorizedLink from '../../auth/AuthorizedLink';
import { FlowSyncAccessPolicy } from '../../../models/FlowSyncAccessPolicy';
import { useTranslation } from 'react-i18next';
import { RequestState } from '../../../models/RequestState';
import ModuleSyncStatus from '../../misc/ModuleSyncStatus';
import ModuleStatusSeverityIcon from '../../misc/ModuleStatusSeverityIcon';

interface HomeModulesProps {
    modules: RequestState<CoreModule[]>
    type: number
}

const HomeModules = (props: HomeModulesProps) => {
    const { t } = useTranslation(['common']);

    const renderActive = (active: boolean) => {
        if (active) {
            return <span title={t('modules.status.activeTitle', { ns: 'common' })}><FontAwesomeIcon icon="toggle-on" className="icon green" /><span className="icon_text">{t('status.active', { ns: 'common' })}</span></span>;
        }
        return <span title={t('modules.status.inActiveTitle', { ns: 'common' })}><FontAwesomeIcon icon="toggle-off" className="icon red" /><span className="icon_text">{t('status.inactive', { ns: 'common' })}</span></span>;
    }

    const renderTriggers = (triggers?: CoreModuleTrigger[], nextStart?: string) => {
        if (!triggers) {
            return "-";
        } if (triggers.length > 1) {
            return t('modules.triggers.multiple', { ns: 'common' });
        } else if (triggers.length === 1) {
            if (triggers[0].triggerType === 2) {
                return (<span>
                    <span>{t('modules.triggers.after', { ns: 'common' })} </span>
                    <AuthorizedLink accessPolicy={FlowSyncAccessPolicy.READ} to={`/module/${triggers[0].runAfterModuleId}/configuration`}>
                        {triggers[0].runAfterModuleTitle}
                    </AuthorizedLink>
                </span>);
            } else if (triggers[0].triggerType === 1) {
                if (nextStart) {
                    return Helper.formatDate(nextStart);
                }
                return t('modules.triggers.time', { ns: 'common' });
            }
        } else {
            return t('modules.triggers.missing', { ns: 'common' });
        }
    }

    const module = (module: CoreModule) => {
        return (
            <div className="dashboard_module" key={module.id}>
                <AuthorizedLink accessPolicy={FlowSyncAccessPolicy.READ} to={module.runnable ? `/module/${module.id}/status` : `/module/${module.id}/configuration`} key={module.id}>
                    <div className="dashboard_top">
                        <h6>{module.title}</h6>
                        {!module.active ? null : <ModuleStatusSeverityIcon status={module.status} />}
                    </div>
                </AuthorizedLink>
                <div className="dashboard_content">
                    {renderActive(module.active)}
                    {module.status?.isInQuarantine && <span title={t('modules.status.quarantineTitle', { ns: 'common' })}><FontAwesomeIcon icon="circle-radiation" className="icon icon_critical" /><span className="icon_text">{t('status.quarantine', { ns: 'common' })}</span></span>}

                    <ModuleSyncStatus
                        status={module.status}
                        displayQuarantine={false}
                        runnable={module.runnable}
                    />
                    {module.runnable && <p>{t('modules.nextRun', { ns: 'common' })}: {renderTriggers(module.moduleTriggers, module.nextScheduledStart)}</p>}
                </div>
            </div>
        );
    }

    const moduleList = (modules: RequestState<CoreModule[]>, type: number) => {
        switch (modules.code) {
            case StatusCode.NONE: return <Loading />;
            case StatusCode.PENDING: return <Loading />;
            case StatusCode.ERROR: return <p>{t('error.getModules', { ns: 'common' })}</p>;
            default: return modules.data.filter(m => m.moduleType === type).map(m => module(m));
        }
    };

    return (<>
        {moduleList(props.modules, props.type)}
    </>);
}

export default HomeModules;
